import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Briefcase, Plus } from 'lucide-react'
import { Page } from '@/components/Page'
import { DataTable } from '@/components/DataTable'
import { Toolbar } from '@/components/Toolbar'
import { EmptyState } from '@/components/EmptyState'
import { FormField } from '@/components/FormField'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { Modal } from '@/components/Modal'
import { useToast } from '@/hooks/useToast'
import { useAuth } from '@/auth/useAuth'
import { useContractors, useCreateContractor } from '@/features/payroll/queries'
import type { Contractor } from '@/features/payroll/types'

export function ContractorsListPage() {
  const navigate = useNavigate()
  const { hasPermission } = useAuth()
  const { toast } = useToast()
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState({
    business_name: '',
    individual_first_name: '',
    individual_last_name: '',
    email: '',
    tax_id_type: 'EIN',
    tax_id_last4: '',
    is_1099_eligible: true,
  })

  const { data, isLoading } = useContractors({ q: search.trim() || undefined })
  const createMut = useCreateContractor()
  const canManage = hasPermission('payroll.contractors.manage')

  const columns = useMemo(
    () => [
      { accessorKey: 'contractor_number', header: 'Contractor #' },
      {
        accessorKey: 'business_name',
        header: 'Name',
        cell: ({ row }: { row: { original: Contractor } }) => {
          const c = row.original
          return (
            <span>
              {c.business_name ||
                `${c.individual_first_name ?? ''} ${c.individual_last_name ?? ''}`.trim() ||
                '-'}
            </span>
          )
        },
      },
      { accessorKey: 'email', header: 'Email' },
      { accessorKey: 'tax_id_type', header: 'TIN type' },
      {
        accessorKey: 'is_1099_eligible',
        header: '1099',
        cell: ({ row }: { row: { original: Contractor } }) => (
          <Badge variant={row.original.is_1099_eligible ? 'success' : 'secondary'}>
            {row.original.is_1099_eligible ? 'Eligible' : 'Excluded'}
          </Badge>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }: { row: { original: Contractor } }) => (
          <Badge variant={row.original.status === 'active' ? 'success' : 'secondary'}>
            {row.original.status}
          </Badge>
        ),
      },
    ],
    [],
  )

  async function onSave() {
    if (!form.business_name && !form.individual_last_name) {
      toast({
        title: 'Name required',
        description: 'Enter either a business name or the individual\'s name.',
        variant: 'destructive',
      })
      return
    }
    try {
      const r = await createMut.mutateAsync(form as never)
      toast({ title: 'Contractor added', description: r.data.business_name || r.data.individual_last_name || '' })
      setOpen(false)
      setForm({
        business_name: '',
        individual_first_name: '',
        individual_last_name: '',
        email: '',
        tax_id_type: 'EIN',
        tax_id_last4: '',
        is_1099_eligible: true,
      })
    } catch (err) {
      toast({
        title: 'Save failed',
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      })
    }
  }

  return (
    <Page
      title="Contractors"
      description="1099 contractors with TIN management and backup-withholding tracking. 1099-NEC and 1099-MISC are generated annually from the platform."
      actions={
        canManage ? (
          <Button onClick={() => setOpen(true)}>
            <Plus className="mr-2 size-4" /> Add contractor
          </Button>
        ) : undefined
      }
    >
      <Toolbar align="start">
        <FormField
          label=""
          placeholder="Search contractors"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-72"
        />
      </Toolbar>
      <DataTable
        columns={columns as never}
        rows={data?.data ?? []}
        loading={isLoading}
        empty={
          <EmptyState
            icon={Briefcase}
            title="No contractors yet"
            description="Add 1099 contractors to track payments, generate year-end forms, and apply backup withholding when required."
            action={
              canManage ? (
                <Button onClick={() => setOpen(true)}>
                  <Plus className="mr-2 size-4" /> Add contractor
                </Button>
              ) : undefined
            }
          />
        }
        onRowClick={(c) => navigate(`/payroll/contractors/${(c as Contractor).id}`)}
      />
      <Modal
        open={open}
        onOpenChange={setOpen}
        title="Add contractor"
        footer={
          <>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={onSave} disabled={createMut.isPending}>
              {createMut.isPending ? 'Saving…' : 'Save contractor'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <FormField
            label="Business name"
            hint="Use the legal business name; otherwise fill the individual name fields."
            value={form.business_name}
            onChange={(e) => setForm({ ...form, business_name: e.target.value })}
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <FormField
              label="Individual first name"
              value={form.individual_first_name}
              onChange={(e) => setForm({ ...form, individual_first_name: e.target.value })}
            />
            <FormField
              label="Individual last name"
              value={form.individual_last_name}
              onChange={(e) => setForm({ ...form, individual_last_name: e.target.value })}
            />
          </div>
          <FormField
            label="Email"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <FormField
              label="TIN type"
              control={
                <select
                  value={form.tax_id_type}
                  onChange={(e) => setForm({ ...form, tax_id_type: e.target.value })}
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="EIN">EIN</option>
                  <option value="SSN">SSN</option>
                  <option value="ITIN">ITIN</option>
                  <option value="FOREIGN">Foreign</option>
                </select>
              }
            />
            <FormField
              label="TIN last 4"
              inputMode="numeric"
              maxLength={4}
              value={form.tax_id_last4}
              onChange={(e) => setForm({ ...form, tax_id_last4: e.target.value.replace(/\D/g, '').slice(0, 4) })}
            />
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Checkbox
              id="contractor-1099-eligible"
              checked={form.is_1099_eligible}
              onCheckedChange={(v) => setForm({ ...form, is_1099_eligible: v === true })}
            />
            <label htmlFor="contractor-1099-eligible">Eligible for 1099-NEC at year end (yearly payments over $600)</label>
          </div>
        </div>
      </Modal>
    </Page>
  )
}
